import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { AgeGroup, Domain, Format } from '@sayyesschool/common/entities/common';
import { EnrollmentStatus, EnrollmentType } from '@sayyesschool/common/entities/enrollment';
// import ScheduleSelect from '@sayyesschool/frontend/components/schedule-select';
// import DateTimeSelect from '@sayyesschool/frontend/components/datetime-select';
import UserSelect from '@sayyesschool/frontend/components/user-select';
import {
    ageGroupOptions,
    domainOptions,
    formatOptions,
    levelOptions,
    purposeOptions,
    teacherTypeOptions
} from '@sayyesschool/frontend/data/common';
import { typeOptions } from '@sayyesschool/frontend/features/enrollments';
import { useManagers } from '@sayyesschool/frontend/features/managers';
import { useTeachers } from '@sayyesschool/frontend/features/teachers';
import { useFormData } from '@sayyesschool/frontend/hooks/form';
import { Flex, Form } from '@sayyesschool/frontend/ui-components';

const defaultData = {
    schedule: [],
    trialLessonSchedule: [],
    info: {}
};

const getFormData = ({
    status = EnrollmentStatus.Processing,
    domain = Domain.General,
    type = EnrollmentType.Individual,
    format = Format.Online,
    ageGroup = AgeGroup.Adults,
    level = 0,
    teacherType = '',
    lessonDuration = 50,
    schedule = [],
    trialLessonSchedule = [],
    info = {
        purpose: '',
        experience: '',
        preferences: '',
        note: ''
    },
    learnerId = '',
    managerId = null,
    teacherId = null,
    hhid = ''
} = defaultData) => ({
    status,
    domain,
    type,
    format,
    ageGroup,
    teacherType,
    level: String(level),
    lessonDuration,
    schedule,
    trialLessonSchedule,
    purpose: info.purpose,
    experience: info.experience,
    preferences: info.preferences,
    note: info.note,
    learnerId,
    managerId,
    teacherId,
    hhid
});

function EnrollmentForm({
    enrollment = {},
    onChange,
    onSubmit,
    ...props
}, ref) {
    const formRef = useRef();

    const [managers] = useManagers();
    const [teachers] = useTeachers();

    const { data, handleChange } = useFormData(
        getFormData(enrollment),
        [enrollment?.updateAt]
    );

    useImperativeHandle(ref, () => ({
        get form() { return formRef.current; },
        get data() { return data; }
    }));

    useEffect(() => {
        const {
            purpose,
            experience,
            preferences,
            note,
            ...rest
        } = data;

        onChange?.({
            ...rest,
            managerId: data.managerId || null,
            teacherId: data.teacherId || null,
            level: Number(data.level),
            info: {
                purpose,
                experience,
                preferences,
                note
            }
        });
    }, [data, onChange]);

    const handleSubmit = useCallback(() => {
        const {
            purpose,
            experience,
            preferences,
            note,
            ...rest
        } = data;

        onSubmit({
            ...rest,
            managerId: data.managerId || null,
            teacherId: data.teacherId || null,
            level: Number(data.level),
            info: {
                purpose,
                experience,
                preferences,
                note
            }
        });
    }, [data, onSubmit]);

    return (
        <Form
            ref={formRef}
            className="EnrollmentForm"
            onSubmit={handleSubmit}
            {...props}
        >
            <Flex
                dir="column"
                gap="sm"
            >
                <Form.Input
                    name="hhid"
                    value={data.hhid}
                    label="Hollihop ID"
                    onChange={handleChange}
                />

                <Form.Select
                    label="Уровень"
                    name="level"
                    value={data.level}
                    options={levelOptions}
                    onChange={handleChange}
                />

                <Form.Select
                    label="Направление"
                    name="domain"
                    value={data.domain}
                    options={domainOptions}
                    required
                    onChange={handleChange}
                />

                <Form.Select
                    label="Тип"
                    name="type"
                    value={data.type}
                    options={typeOptions}
                    required
                    onChange={handleChange}
                />

                <Form.Select
                    label="Формат"
                    name="format"
                    value={data.format}
                    options={formatOptions}
                    required
                    onChange={handleChange}
                />

                <Form.Select
                    label="Возрастная группа"
                    name="ageGroup"
                    value={data.ageGroup}
                    options={ageGroupOptions}
                    onChange={handleChange}
                />

                <Form.Select
                    label="Тип преподавателя"
                    name="teacherType"
                    value={data.teacherType}
                    options={teacherTypeOptions}
                    onChange={handleChange}
                />

                <Form.Input
                    label="Продолжительность урока"
                    name="lessonDuration"
                    value={data.lessonDuration}
                    type="number"
                    onChange={handleChange}
                />

                <Form.Select
                    label="Цель"
                    name="purpose"
                    value={data.purpose}
                    options={purposeOptions}
                    onChange={handleChange}
                />

                <Form.Textarea
                    label="Опыт"
                    name="experience"
                    value={data.experience}
                    onChange={handleChange}
                />

                <Form.Textarea
                    label="Пожелания"
                    name="preferences"
                    value={data.preferences}
                    onChange={handleChange}
                />

                {/* <DateTimeSelect
                label="Пробный урок"
                name="trialLesson"
                items={data.trialLessonSchedule}
                onChange={handleChange}
            />

            <ScheduleSelect
                label="Расписание"
                name="schedule"
                schedule={data.schedule}
                onChange={handleChange}
            /> */}

                <Form.Textarea
                    label="Примечание"
                    name="note"
                    value={data.note}
                    onChange={handleChange}
                />

                {enrollment.id &&
                    <>
                        <UserSelect
                            label="Менеджеры"
                            name="managerId"
                            value={data.managerId}
                            options={managers?.map(manager => ({
                                key: manager.id,
                                value: manager.id,
                                label: manager.fullname
                            }))}
                            onChange={handleChange}
                        />

                        <UserSelect
                            label="Преподаватели"
                            name="teacherId"
                            value={data.teacherId}
                            options={teachers?.map(teacher => ({
                                key: teacher.id,
                                value: teacher.id,
                                label: teacher.fullname
                            }))}
                            onChange={handleChange}
                        />
                    </>
                }
            </Flex>
        </Form>
    );
}

export default forwardRef(EnrollmentForm);